import { PropsWithChildren, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from './auth/useAuth';
import { AuthGuard } from './auth/AuthGuard';
import LoginPage from './auth/LoginPage';
import RegisterPage from './auth/RegisterPage';
import VerifyEmailPage from './auth/VerifyEmailPage';
import ForgotPasswordPage from './auth/ForgotPasswordPage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import { AppShell } from './shell/AppShell';
import SystemOverviewPage from './pages/SystemOverview/SystemOverviewPage';
import TradingPositionsPage from './pages/Trading/TradingPositionsPage';
import TradingLiveMonitorPage from './pages/Trading/TradingLiveMonitorPage';
import TradingUnfilledPage from './pages/Trading/TradingUnfilledPage';
import TradingEventStreamPage from './pages/Trading/TradingEventStreamPage';
import TradingBacktestResultsPage from './pages/Trading/TradingBacktestResultsPage';
import SignalForecastPage from './pages/SignalForecast/SignalForecastPage';
import AgentHistoryPage from './pages/AgentHistory/AgentHistoryPage';
import LiveMonitorPage from './pages/LiveMonitor/LiveMonitorPage';
import DataExplorerPage from './pages/DataExplorer/DataExplorerPage';
import DisclosuresPage from './pages/DataExplorer/DisclosuresPage';
import StrategyInsightsPage from './pages/StrategyInsights/StrategyInsightsPage';
import SystemLogPage from './pages/SystemLog/SystemLogPage';
import AlertSettingsPage from './pages/AlertSettings/AlertSettingsPage';
import PendingCAEventsPage from './pages/PendingCAEvents/PendingCAEventsPage';
import SectorIntelligencePage from './pages/SectorIntelligence/SectorIntelligencePage';
import SectorGroupsPage from './pages/SectorIntelligence/SectorGroupsPage';
import SectorTaxonomyPage from './pages/SectorTaxonomy/SectorTaxonomyPage';
import SectorInsightsPage from './pages/SectorInsights/SectorInsightsPage';
import ProfilePage from './pages/Profile/ProfilePage';
import GovernanceLivePage from './pages/GovernanceLive/GovernanceLivePage';
import GovernanceSLAPage from './pages/GovernanceSLA/GovernanceSLAPage';
import RiskHaltPage from './pages/RiskHalt/RiskHaltPage';
import MarketDataStatusPage from './pages/MarketDataStatus/MarketDataStatusPage';
import StageOverridePage from './pages/StageOverride/StageOverridePage';
import LeadingSectorsPage from './pages/LeadingSectors/LeadingSectorsPage';
import LeadingStocksPage from './pages/LeadingStocks/LeadingStocksPage';
import StockScoreExplorerPage from './pages/StockScoreExplorer/StockScoreExplorerPage';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Bootstrapper({ children }: PropsWithChildren): JSX.Element {
  const { bootstrap } = useAuth();
  useEffect(() => {
    bootstrap();
    // 부트스트랩은 마운트 시 1회만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Bootstrapper>
          <Routes>
            {/* 비인증 (public) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* 인증 필수 (AppShell) */}
            <Route
              element={
                <AuthGuard>
                  <AppShell />
                </AuthGuard>
              }
            >
              <Route index element={<SystemOverviewPage />} />
              <Route path="/positions" element={<TradingPositionsPage />} />
              {/* 트레이딩 도메인 화면군 (UNIMPLEMENTED_INVENTORY §4.3 권고) */}
              <Route path="/trading/live" element={<TradingLiveMonitorPage />} />
              <Route path="/trading/unfilled" element={<TradingUnfilledPage />} />
              <Route path="/trading/events" element={<TradingEventStreamPage />} />
              <Route path="/trading/backtest" element={<TradingBacktestResultsPage />} />
              <Route path="/trading/backtest/:id" element={<TradingBacktestResultsPage />} />
              <Route path="/signals" element={<SignalForecastPage />} />
              <Route path="/history" element={<AgentHistoryPage />} />
              <Route path="/history/:eventId" element={<AgentHistoryPage />} />
              <Route path="/live" element={<LiveMonitorPage />} />
              <Route path="/data" element={<DataExplorerPage />} />
              <Route path="/data/disclosures" element={<DisclosuresPage />} />
              <Route path="/strategy" element={<StrategyInsightsPage />} />
              <Route path="/logs" element={<SystemLogPage />} />
              <Route path="/logs/:eventId" element={<SystemLogPage />} />
              <Route path="/settings/alerts" element={<AlertSettingsPage />} />
              <Route path="/settings/profile" element={<ProfilePage />} />
              <Route path="/pending-ca-events" element={<PendingCAEventsPage />} />
              <Route
                path="/sector-intelligence"
                element={<Navigate to="/sector-intelligence/sectors" replace />}
              />
              <Route path="/sector-intelligence/sectors" element={<SectorGroupsPage />} />
              <Route path="/sector-intelligence/stocks" element={<SectorIntelligencePage />} />
              <Route path="/sectors/taxonomy" element={<SectorTaxonomyPage />} />
              <Route path="/sectors/insights" element={<SectorInsightsPage />} />
              {/* v1.8 — 화면 12~16 */}
              <Route path="/governance/live" element={<GovernanceLivePage />} />
              <Route path="/governance/sla" element={<GovernanceSLAPage />} />
              <Route path="/governance/stage-override" element={<StageOverridePage />} />
              <Route path="/risk/halt" element={<RiskHaltPage />} />
              <Route path="/data/market-data-status" element={<MarketDataStatusPage />} />
              {/* v1.10 — 화면 17 (Phase A, 주도주 도메인 화면군) */}
              <Route path="/leading/sectors" element={<LeadingSectorsPage />} />
              {/* v1.10 — 화면 18 (Phase B, LeaderStocks45 Grid) */}
              <Route path="/leading/stocks" element={<LeadingStocksPage />} />
              {/* v1.10 — 화면 19 (Phase C, Stock Score Explorer) */}
              <Route path="/leading/scores" element={<StockScoreExplorerPage />} />
            </Route>

            {/* fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Bootstrapper>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
